/**
 * Conversation List Component
 * Displays list of conversations in sidebar - WhatsApp style
 */

import React, { useState, useEffect } from 'react';
import { Plus, Search, Users, MessageSquare, MoreVertical, BellOff } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { messagingService } from '@/firebase/messagingService';
import { Conversation } from '@/types/messaging';
import NewConversationModal from './NewConversationModal';
import { userService } from '@/firebase/database';
import { isSystemManager, isManagerOrAdminInAnyOrganization } from '@/utils/roleHelpers';
import Logo from '../Logo';

interface ConversationListProps {
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
}

interface UserInfo {
  id: string;
  name: string;
  email: string;
  picture?: string;
}

const ConversationList: React.FC<ConversationListProps> = ({
  onSelectConversation,
  selectedConversationId,
}) => {
  const { user } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userDataForRoles, setUserDataForRoles] = useState<{ role?: string; organizationRoles?: Record<string, { systemRole?: string; role?: string }> } | null>(null);
  const [userNamesMap, setUserNamesMap] = useState<Map<string, UserInfo>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'groups' | 'context'>('all');
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map());
  const [mutedConversations, setMutedConversations] = useState<Set<string>>(new Set());

  const organizationIds: string[] = organizations?.map((o) => o.id).filter((id): id is string => id != null) ?? [];
  const orgIdToName = new Map<string, string>();
  organizations?.forEach((o) => {
    if (o.id && o.name) orgIdToName.set(o.id, o.name);
  });

  // Load user role and full user data to determine if they can create groups (legacy role or admin/manager in any org)
  useEffect(() => {
    const loadUserRole = async () => {
      if (user?.id) {
        try {
          const userResult = await userService.getUser(user.id);
          if (userResult.success && userResult.data) {
            const data = userResult.data;
            setUserRole(data.role || null);
            setUserDataForRoles({
              role: data.role,
              organizationRoles: data.organizationRoles,
            });
          } else {
            setUserDataForRoles(null);
          }
        } catch (error) {
          console.error('Error loading user role:', error);
          setUserDataForRoles(null);
        }
      }
    };
    loadUserRole();
  }, [user?.id]);

  // Subscribe to conversations (all organizations for common messages screen)
  useEffect(() => {
    if (!user?.id) {
      setConversations([]);
      setLoading(false);
      return;
    }

    if (organizationIds.length === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = messagingService.subscribeToConversationsAllOrgs(
      user.id,
      organizationIds,
      (conversationsList) => {
        setConversations(conversationsList);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.id, organizationIds.join(',')]);

  // Fetch user info for participants
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!conversations.length) return;

      const userIds = new Set<string>();
      conversations.forEach((conv) => {
        conv.participants?.forEach((id) => {
          if (id !== user?.id) userIds.add(id);
        });
      });

      const infoMap = new Map<string, UserInfo>();
      const fetchPromises = Array.from(userIds).map(async (userId) => {
        try {
          const userResult = await userService.getUser(userId);
          if (userResult.success && userResult.data) {
            infoMap.set(userId, {
              id: userId,
              name: userResult.data.name || userResult.data.email || 'Unknown User',
              email: userResult.data.email || '',
              picture: userResult.data.picture,
            });
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
        }
      });

      await Promise.all(fetchPromises);
      setUserNamesMap(infoMap);
    };

    fetchUserInfo();
  }, [conversations, user?.id]);

  // Subscribe to unread counts and mute status for all conversations
  useEffect(() => {
    if (!user?.id || !conversations.length) {
      setUnreadCounts(new Map());
      setMutedConversations(new Set());
      return;
    }

    const unsubscribes: (() => void)[] = [];
    const countsMap = new Map<string, number>();
    const mutedSet = new Set<string>();

      conversations.forEach((conv) => {
        if (!conv.id) return;

        // Subscribe to participant document for real-time unread count and mute status updates
        const participantId = `${conv.id}_${user.id}`;
        const participantRef = doc(db, 'conversationParticipants', participantId);
      const unsubscribe = onSnapshot(
        participantRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            const count = data.unreadCount || 0;
            const muted = data.muted === true;
            countsMap.set(conv.id!, count);
            if (muted) {
              mutedSet.add(conv.id!);
            } else {
              mutedSet.delete(conv.id!);
            }
            setUnreadCounts(new Map(countsMap));
            setMutedConversations(new Set(mutedSet));
          } else {
            countsMap.set(conv.id!, 0);
            mutedSet.delete(conv.id!);
            setUnreadCounts(new Map(countsMap));
            setMutedConversations(new Set(mutedSet));
          }
        },
        (error) => {
          console.error(`Error subscribing to participant data for ${conv.id}:`, error);
        }
      );

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [conversations, user?.id]);

  const getConversationDisplayName = (conversation: Conversation): string => {
    // Check for group name first (works for both 'group' and 'task' type conversations that have a name)
    if (conversation.name) {
      return conversation.name;
    }

    // For direct conversations or groups without names, get participant name
    if ((conversation.type === 'direct' || conversation.type === 'task' || conversation.type === 'contract' || conversation.type === 'checklist' || conversation.type === 'form') && conversation.participants) {
      // If it's a direct conversation or linked conversation, try to get the other participant
      const otherParticipantId = conversation.participants.find(id => id !== user?.id);
      if (otherParticipantId) {
        const userInfo = userNamesMap.get(otherParticipantId);
        if (userInfo?.name) {
          return userInfo.name;
        }
      }
      
      // If it's a group-like conversation (multiple participants) but no name, show participant count
      if (conversation.participants.length > 2) {
        return `${conversation.participants.length} participants`;
      }
      
      // For direct conversations without name
      if (conversation.type === 'direct') {
        return 'Direct Message';
      }
    }

    // For groups without names (shouldn't happen, but fallback)
    if (conversation.type === 'group') {
      return 'Group';
    }

    return 'Unknown';
  };

  const getConversationAvatar = (conversation: Conversation): string | undefined => {
    if (conversation.type === 'direct' && conversation.participants) {
      const otherParticipantId = conversation.participants.find(id => id !== user?.id);
      if (otherParticipantId) {
        const userInfo = userNamesMap.get(otherParticipantId);
        return userInfo?.picture;
      }
    }
    return undefined;
  };

  const getConversationPreview = (conversation: Conversation): string => {
    if (conversation.lastMessage) {
      const sender = conversation.lastMessage.sentBy === user?.id ? 'You: ' : '';
      return sender + (conversation.lastMessage.text || '');
    }
    return 'No messages yet';
  };

  // Filter conversations
  const filteredConversations = conversations.filter((conv) => {
    // Search filter
    if (searchQuery) {
      const displayName = getConversationDisplayName(conv).toLowerCase();
      const preview = getConversationPreview(conv).toLowerCase();
      if (!displayName.includes(searchQuery.toLowerCase()) && !preview.includes(searchQuery.toLowerCase())) {
        return false;
      }
    }

    // Type filter
    if (activeFilter === 'groups') {
      // Show only user-created groups (not system-generated context conversations)
      return conv.type === 'group' && !conv.linkedItems;
    }
    if (activeFilter === 'context') {
      // Show conversations with contextual types (task, contract, checklist, form) or linked items
      return conv.type === 'task' || 
             conv.type === 'contract' || 
             conv.type === 'checklist' || 
             conv.type === 'form' ||
             !!(conv.linkedItems?.taskId || conv.linkedItems?.contractId || conv.linkedItems?.vendorId);
    }
    if (activeFilter === 'unread') {
      return (unreadCounts.get(conv.id || '') || 0) > 0;
    }

    return true;
  });

  const canCreateGroup =
    (userRole != null && isSystemManager(userRole)) ||
    (userDataForRoles != null &&
      organizationIds.length > 0 &&
      isManagerOrAdminInAnyOrganization(userDataForRoles as Parameters<typeof isManagerOrAdminInAnyOrganization>[0], organizationIds));

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <Logo size="md" showText={true} />
          <button
            onClick={() => setShowNewConversationModal(true)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            title="New conversation"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search or start new chat"
            className="w-full pl-10 pr-4 py-2 bg-white rounded-lg text-sm focus:outline-none focus:ring-0"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex space-x-1">
        <button
          onClick={() => setActiveFilter('all')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeFilter === 'all'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveFilter('unread')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors relative ${
            activeFilter === 'unread'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Unread
        </button>
        <button
          onClick={() => setActiveFilter('groups')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeFilter === 'groups'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Groups
        </button>
        <button
          onClick={() => setActiveFilter('context')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            activeFilter === 'context'
              ? 'bg-gray-900 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Context
        </button>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <p>Loading conversations...</p>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <p className="mb-2">No conversations found</p>
            <button
              onClick={() => setShowNewConversationModal(true)}
              className="text-gray-800 hover:text-gray-900 text-sm font-medium"
            >
              Start a conversation
            </button>
          </div>
        ) : (
          <div>
            {filteredConversations.map((conversation) => {
              const isSelected = conversation.id === selectedConversationId;
              const displayName = getConversationDisplayName(conversation);
              const preview = getConversationPreview(conversation);
              const avatar = getConversationAvatar(conversation);
              const unreadCount = unreadCounts.get(conversation.id || '') || 0;
              const isMuted = mutedConversations.has(conversation.id || '');

              return (
                <button
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                    isSelected ? 'bg-[#f0f2f5]' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={displayName}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : conversation.type === 'group' ? (
                        <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                          <MessageSquare className="w-6 h-6 text-gray-600" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            isSelected ? 'text-gray-900' : 'text-gray-900'
                          }`}>
                            {displayName}
                          </p>
                          {isMuted && (
                            <span title="Muted" className="flex-shrink-0">
                              <BellOff className="w-4 h-4 text-gray-500" />
                            </span>
                          )}
                        </div>
                        {conversation.lastMessage?.sentAt && (
                          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                            {formatMessageTime(conversation.lastMessage.sentAt)}
                          </span>
                        )}
                      </div>
                      {conversation.type !== 'direct' && conversation.organizationId && orgIdToName.has(conversation.organizationId) && (
                        <p className="text-xs text-gray-500 truncate mb-0.5">
                          {orgIdToName.get(conversation.organizationId)}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${
                          unreadCount > 0 ? 'font-medium text-gray-900' : 'text-gray-500'
                        }`}>
                          {preview}
                        </p>
                        {unreadCount > 0 && (
                          <span className="ml-2 bg-gray-800 text-white text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center flex-shrink-0">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <NewConversationModal
          onClose={() => setShowNewConversationModal(false)}
          onConversationCreated={(conversation) => {
            setShowNewConversationModal(false);
            onSelectConversation(conversation);
          }}
          canCreateGroup={canCreateGroup || false}
        />
      )}
    </div>
  );
};

/**
 * Format message timestamp for display (WhatsApp style)
 */
const formatMessageTime = (timestamp: any): string => {
  if (!timestamp) return '';

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Today
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    
    // Yesterday
    if (diffDays === 1) {
      return 'Yesterday';
    }
    
    // This week
    if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }

    // Older
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (error) {
    return '';
  }
};

export default ConversationList;
